import {
  WaitlistSeatsForParentQuery,
  WaitlistSeatsForParentQueryVariables,
  WaitlistSummaryForActivityQuery,
  WaitlistSummaryForActivityQueryVariables,
} from "@outschool/gql-frontend-generated/src";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";

import LearnerFragment from "./LearnerFragment";
import { SectionFragment, SectionLeaderFragment } from "./SectionFragments";

export const WaitlistSeatsForParent = gql`
  query WaitlistSeatsForParent($parentUid: ID!, $sectionUid: ID) {
    waitlistSeatsForParent(userUid: $parentUid, sectionUid: $sectionUid) {
      uid
      learner {
        ...LearnerFragment
      }
      enrollment {
        uid
      }
      section {
        uid
        filledSpaceCount
        size_max
        published_at
        canceled_at
        deleted_at
      }
    }
  }
  ${LearnerFragment}
`;

export function useWaitlistSeatsForParentQuery({
  parentUid = "",
  sectionUid,
  skip = false,
}: {
  parentUid?: string;
  sectionUid?: string;
  skip?: boolean;
}) {
  const { loading, error, data, refetch } = useQueryWithPreviousData<
    WaitlistSeatsForParentQuery,
    WaitlistSeatsForParentQueryVariables
  >(WaitlistSeatsForParent, {
    fetchPolicy: "cache-first",
    variables: {
      parentUid,
      sectionUid,
    },
    skip: skip || !parentUid,
  });

  const waitlistSeats =
    data?.waitlistSeatsForParent?.filter(
      seat => !seat?.section?.deleted_at && !seat?.section?.canceled_at,
    ) ?? [];

  return {
    loading,
    error,
    refetch,
    waitlistSeats,
  };
}

export const WaitlistSummaryForActivity = gql`
  query WaitlistSummaryForActivity($activityUid: ID!) {
    activity(uid: $activityUid) {
      uid
      allowWaitlist
      isClub
      weekly_meetings
      duration_weeks
      is_ongoing_weekly
      published_at
      hasTeacherSchedule
      sections {
        ...SectionFragment
        ...SectionLeaderFragment
        filledSpaceCount
        waitlistSeatsCount
        leader {
          uid
          name
        }
      }
    }
  }
  ${SectionFragment}
  ${SectionLeaderFragment}
`;

export function useWaitlistSummaryForActivitySections({
  activityUid,
  skip = false,
}: {
  activityUid: string;
  skip?: boolean;
}) {
  const { loading, error, data, refetch } = useQueryWithPreviousData<
    WaitlistSummaryForActivityQuery,
    WaitlistSummaryForActivityQueryVariables
  >(WaitlistSummaryForActivity, {
    fetchPolicy: "cache-first",
    variables: {
      activityUid,
    },
    skip: skip,
  });

  const sections =
    data?.activity?.sections?.filter(section => section.waitlistSeatsCount) ??
    [];

  return {
    loading,
    error,
    refetch,
    sections,
  };
}
